import { useRef, type RefObject } from 'react'

import type { Channel, SavedReply } from '~publish/gql/graphql'

import { useReplyFeatures } from './useReplyFeatures'
import { useReplyForm } from './useReplyForm'
import { useReplyInteractions } from './useReplyInteractions'
import { useReplySubmission } from './useReplySubmission'
import type { FilterStatus } from '../../CommentsFilters/types'

type UseCommentReplyProps = {
  channel: Channel
  currentStatusFilter?: FilterStatus
  onAnimateOut: (shouldAnimate: boolean) => void
  textareaRef: RefObject<HTMLTextAreaElement>
  initialExpanded?: boolean
  onExpandNextComment?: () => void
  onManualExpansion?: (commentId: string | null) => void
}

type UseCommentReplyReturn = {
  form: {
    text: string
    setText: (text: string) => void
    isExpanded: boolean
    expand: () => void
    focus: () => void
    clear: () => void
  }
  submission: {
    isSubmitting: boolean
    isOptimistic: boolean
    error?: string
    errorType?: string
    pendingReply?: string
    submit: (text?: string) => Promise<void>
    retry: () => Promise<void>
  }
  ui: {
    showDiscardDialog: boolean
    setShowDiscardDialog: (show: boolean) => void
    discard: () => void
    keepWriting: () => void
  }
  features: {
    insertSuggestion: (text: string) => void
    insertSavedReply: (savedReply: SavedReply) => void
    savedRepliesRef: RefObject<HTMLDivElement>
    emojiPickerRef: RefObject<HTMLDivElement>
  }
  refs: {
    formRef: RefObject<HTMLFormElement>
    textareaRef: RefObject<HTMLTextAreaElement>
    suggestionsRef: RefObject<HTMLDivElement>
  }
}

/**
 * Main orchestrator hook for comment reply functionality.
 *
 * This hook coordinates all aspects of the comment reply experience by combining:
 * - Form state management (text, expansion, focus)
 * - Submission logic (API calls, optimistic updates, error handling)
 * - UI interactions (click outside, keyboard shortcuts, discard dialog)
 * - Feature integrations (saved replies, suggestions)
 *
 * **When to use:**
 * - As the primary hook for any comment reply form component
 * - When you need a complete reply experience with all features
 * - For components that require full reply functionality (form, submission, interactions)
 *
 * **Key Features:**
 * - Optimistic UI updates with fallback on API failure
 * - Automatic engagement tracking and animation control
 * - Saved replies integration with analytics
 * - Form validation and error handling
 * - Keyboard shortcuts (Cmd/Ctrl + Enter to submit)
 * - Click outside behavior with discard protection
 *
 * @param props - Configuration object for the reply experience
 * @returns Complete reply functionality organized by concern
 *
 * @example
 * ```tsx
 * const reply = useCommentReply({
 *   channel,
 *   currentStatusFilter,
 *   onAnimateOut: setShouldAnimateOut,
 *   textareaRef,
 *   initialExpanded: false
 * })
 *
 * const { form, submission, ui, features } = reply
 *
 * // Basic usage
 * <textarea value={form.text} onChange={(e) => form.setText(e.target.value)} />
 * <button onClick={() => submission.submit()} disabled={submission.isSubmitting}>
 *   {submission.isSubmitting ? 'Sending...' : 'Reply'}
 * </button>
 * ```
 *
 * @example
 * ```tsx
 * // Feature integrations
 * features.insertSavedReply(savedReply)
 * features.insertSuggestion(suggestion)
 *
 * // UI interactions
 * <DiscardDialog open={ui.showDiscardDialog} onDiscard={ui.discard} />
 * ```
 */
export const useCommentReply = ({
  channel,
  currentStatusFilter,
  onAnimateOut,
  textareaRef,
  initialExpanded = false,
  onExpandNextComment,
  onManualExpansion,
}: UseCommentReplyProps): UseCommentReplyReturn => {
  // Refs
  const formRef = useRef<HTMLFormElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  // Form state management
  const form = useReplyForm({
    textareaRef,
    initialExpanded,
    onManualExpansion,
  })

  // Submission logic
  const submission = useReplySubmission({
    channel,
    currentStatusFilter,
    onAnimateOut,
    onFormClear: form.clear,
    onFormRestore: form.setText,
    onFormCollapse: form.collapse,
    getCurrentText: () => form.text,
    onReplySubmitted: onExpandNextComment,
  })

  // Feature integrations
  const features = useReplyFeatures({
    text: form.text,
    setText: form.setText,
    textareaRef,
  })

  // UI interactions
  const interactions = useReplyInteractions({
    formRef,
    textareaRef,
    savedRepliesRef: features.savedRepliesRef,
    emojiPickerRef: features.emojiPickerRef,
    suggestionsRef,
    isExpanded: form.isExpanded,
    text: form.text,
    isSubmitting: submission.isSubmitting,
    onDiscard: () => {
      form.clear()
      form.collapse()
    },
    onKeepWriting: () => {
      // Use requestAnimationFrame to ensure focus happens after dialog is closed
      requestAnimationFrame(() => {
        textareaRef.current?.focus()
      })
    },
    onSubmit: async () => {
      // Add form validation
      if (formRef.current && !formRef.current.checkValidity()) {
        formRef.current.reportValidity()
        return
      }
      await submission.submit()
    },
  })

  return {
    form,
    submission,
    ui: interactions,
    features,
    refs: {
      formRef,
      textareaRef,
      suggestionsRef,
    },
  }
}
